import { MoreHorizontal, Repeat, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  apptsByResourceForDay,
  apptsForDay,
  blockForCell,
  dateKeyOf,
  deriveSlot,
  gridAppts,
  gridHours,
  personBondLine,
  statusMeta,
} from './agenda-adapter';
import { ResourceBlocksModal } from './ResourceBlocksModal';

import type {
  AppointmentWireItem,
  SchedulingBlockWire,
  SchedulingResourceWire,
} from '../../lib/api-client';
import type { JSX } from 'react';

const RES_TYPE_LABEL: Record<SchedulingResourceWire['resourceType'], string> = {
  professional: 'Profissional',
  chair: 'Cadeira',
  room: 'Sala',
  equipment: 'Equipamento',
  other: 'Recurso',
};

/** Agrupa os appointments de um recurso (numa hora) por horário de início HH:MM. */
function byStart(appts: AppointmentWireItem[]): Map<string, AppointmentWireItem[]> {
  const m = new Map<string, AppointmentWireItem[]>();
  for (const a of appts) {
    const k = deriveSlot(a).start;
    const list = m.get(k) ?? [];
    list.push(a);
    m.set(k, list);
  }
  return m;
}

function ApptTags({ appt }: { appt: AppointmentWireItem }): JSX.Element | null {
  const isSeries = appt.seriesId !== null;
  const viaAi = appt.source === 'ai';
  if (!isSeries && !viaAi) return null;
  return (
    <div className="flex flex-wrap gap-1" style={{ marginTop: 2 }}>
      {isSeries && (
        <span className="resgrid__tag">
          <Repeat size={11} aria-hidden /> série
        </span>
      )}
      {viaAi && (
        <span className="resgrid__tag resgrid__tag--ai">
          <Sparkles size={11} aria-hidden /> via IA
        </span>
      )}
    </div>
  );
}

/**
 * ResourceDayGrid — Frente B Sessão 1 (UI recurso-cêntrica, gated `scheduling-native`).
 *
 * A grade do DIA por RECURSO × TEMPO (substitui a grade operador-cêntrica no modo nativo):
 * cada coluna é um recurso (profissional/sala/cadeira/equip.), as linhas são horas. Os
 * appointments são colocados na coluna do recurso que ocupam (`appointmentResources`).
 * Recurso de GRUPO (capacidade > 1) mostra o medidor de ocupação "N/capacidade" por slot;
 * lotado fica vermelho. Recurso individual (capacidade 1) mostra o card do agendamento.
 */
export function ResourceDayGrid({
  resources,
  appts: allAppts,
  blocks = [],
  cursorMs,
  onAppt,
}: {
  resources: SchedulingResourceWire[];
  appts: AppointmentWireItem[];
  /** Períodos fechados (AvailabilityBlock) cross-resource — pintados inline na grade. */
  blocks?: SchedulingBlockWire[];
  cursorMs: number;
  onAppt: (appt: AppointmentWireItem) => void;
}): JSX.Element {
  const dateKey = dateKeyOf(cursorMs);
  // N2-09: elo reagendado não vira bloco (nem estende `gridHours`).
  const appts = useMemo(() => gridAppts(allAppts), [allAppts]);
  const { byResource } = useMemo(() => apptsByResourceForDay(appts, dateKey), [appts, dateKey]);
  const dayAppts = useMemo(() => apptsForDay(appts, dateKey), [appts, dateKey]);
  const hours = useMemo(() => gridHours(dayAppts, [dateKey]), [dayAppts, dateKey]);
  const [menuResource, setMenuResource] = useState<SchedulingResourceWire | null>(null);

  const cols = resources.filter((r) => r.id !== '');

  if (cols.length === 0) {
    return (
      <div className="p-6 text-center text-sm" style={{ color: 'var(--fg-3)' }}>
        Nenhum recurso cadastrado neste tenant. Cadastre recursos na configuração da agenda para ver
        a disponibilidade por recurso.
      </div>
    );
  }

  return (
    <>
      <div
        className="resgrid"
        style={{ gridTemplateColumns: `56px repeat(${String(cols.length)}, minmax(150px, 1fr))` }}
      >
        <div className="resgrid__corner" />
        {cols.map((r) => (
          <div key={r.id} className="resgrid__head">
            <div className="resgrid__head-top">
              <span className="resgrid__name">{r.name}</span>
              <button
                type="button"
                aria-label={`Disponibilidade de ${r.name}`}
                title="Fechar período · disponibilidade"
                onClick={() => setMenuResource(r)}
                style={{
                  display: 'inline-flex',
                  border: 0,
                  background: 'transparent',
                  color: 'var(--fg-3)',
                  cursor: 'pointer',
                  padding: 2,
                }}
              >
                <MoreHorizontal size={16} aria-hidden />
              </button>
            </div>
            <span className="resgrid__meta">
              {r.capacity > 1 ? 'Grupo' : RES_TYPE_LABEL[r.resourceType]}
            </span>
            <span className="resgrid__cap">capacidade {r.capacity}</span>
          </div>
        ))}

        {hours.map((h) => (
          <ResourceRow
            key={h}
            hour={h}
            cols={cols}
            byResource={byResource}
            blocks={blocks}
            cursorMs={cursorMs}
            onAppt={onAppt}
          />
        ))}
      </div>

      {menuResource !== null && (
        <ResourceBlocksModal resource={menuResource} onClose={() => setMenuResource(null)} />
      )}
    </>
  );
}

function ResourceRow({
  hour,
  cols,
  byResource,
  blocks,
  cursorMs,
  onAppt,
}: {
  hour: number;
  cols: SchedulingResourceWire[];
  byResource: Map<string, AppointmentWireItem[]>;
  blocks: SchedulingBlockWire[];
  cursorMs: number;
  onAppt: (appt: AppointmentWireItem) => void;
}): JSX.Element {
  return (
    <>
      <div className="resgrid__hour">{String(hour).padStart(2, '0')}:00</div>
      {cols.map((r) => {
        const cellAppts = (byResource.get(r.id) ?? []).filter(
          (a) => deriveSlot(a).startHour === hour,
        );
        const block = blockForCell(blocks, r.id, cursorMs, hour);
        // Período fechado numa célula SEM agendamento → pinta "Fechado" (o motor não oferta
        // aqui; a garantia é do backend, a grade só reflete). Com agendamento (borda rara de
        // bloqueio sobre appt existente), o card do agendamento prevalece.
        if (block !== null && cellAppts.length === 0) {
          return (
            <div key={r.id} className="resgrid__cell">
              <div
                className="resgrid__blocked"
                title={block.reason !== null ? `Fechado · ${block.reason}` : 'Período fechado'}
              >
                Fechado{block.reason !== null ? ` · ${block.reason}` : ''}
              </div>
            </div>
          );
        }
        return (
          <div key={r.id} className="resgrid__cell">
            {cellAppts.length === 0 && <div className="resgrid__empty" />}
            {cellAppts.length > 0 && r.capacity > 1
              ? [...byStart(cellAppts).entries()].map(([start, group]) => {
                  const occupied = group.length;
                  const full = occupied >= r.capacity;
                  return (
                    <button
                      key={start}
                      type="button"
                      className={`resgrid__group${full ? ' resgrid__group--full' : ''}`}
                      onClick={() => group[0] !== undefined && onAppt(group[0])}
                      title={`${String(occupied)} de ${String(r.capacity)} ocupadas`}
                    >
                      <div className="resgrid__group-top">
                        <span className="resgrid__group-title">{group[0]?.title ?? 'Grupo'}</span>
                        <span
                          className={`resgrid__group-count${full ? ' resgrid__group-count--full' : ''}`}
                        >
                          {full
                            ? `Lotado · ${String(occupied)}/${String(r.capacity)}`
                            : `${String(occupied)}/${String(r.capacity)}`}
                        </span>
                      </div>
                      <div className="resgrid__meter">
                        {Array.from({ length: r.capacity }, (_, i) => (
                          <span
                            key={i}
                            className={`resgrid__seg${i < occupied ? ' resgrid__seg--on' : ''}${i < occupied && full ? ' resgrid__seg--full' : ''}`}
                          />
                        ))}
                      </div>
                    </button>
                  );
                })
              : cellAppts.map((a) => {
                  const slot = deriveSlot(a);
                  const meta = statusMeta(a.status);
                  const line = personBondLine(a);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      className={`appt-card appt-card--${meta.tone}`}
                      onClick={() => onAppt(a)}
                    >
                      <span className="appt-card__time">
                        {slot.start}–{slot.end}
                      </span>
                      <span className="appt-card__title">{a.title}</span>
                      {line !== '' && <span className="appt-card__person">{line}</span>}
                      <ApptTags appt={a} />
                    </button>
                  );
                })}
          </div>
        );
      })}
    </>
  );
}
